import React, { useState, useEffect } from 'react';
import { Plus, Trash, X, Upload, Image as ImageIcon, Edit, GripVertical, Save } from 'lucide-react';
import './Banners.css';
import { bannerAPI } from '../../../config/banner-api';
import { productsAPI } from '../../../config/product-api';
import { toast } from 'react-toastify';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import { helpers } from '../../../utils/helpers';
import ImageCropperModal from '../../Common/ImageCropperModal/ImageCropperModal';
import { formatPrice } from '../../../utils/orderDisplay';
import { getAdminRegion } from '../../../constants/stores';

const emptyForm = {
  title: '',
  arabicTitle: '',
  type: 'banner_slider',
  image: null,
  imagePreview: null,
  icon: null,
  iconPreview: null,
  clearIcon: false,
  description: '',
  color: '',
  applicationType: 'none',
  products: [],
  isActive: true,
  link: '',
  region: 'qatar',
};

const Banners = ({ adminData }) => {
  const canView = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_BANNERS);
  const canCreate = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_BANNERS);
  const canEdit = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_BANNERS);
  const canDelete = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.DELETE_BANNERS);
  const storeRegion = getAdminRegion(adminData);

  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [banners, setBanners] = useState([]);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerToDelete, setBannerToDelete] = useState(null);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [draggedBanner, setDraggedBanner] = useState(null);
  const [dragOverBanner, setDragOverBanner] = useState(null);
  const [loadingBanners, setLoadingBanners] = useState(false);
  const [savingBanner, setSavingBanner] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [cropperSrc, setCropperSrc] = useState(null);

  const [formData, setFormData] = useState(emptyForm);
  const [allSelectedProducts, setAllSelectedProducts] = useState([]);
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const [isProductViewDialogOpen, setIsProductViewDialogOpen] = useState(false);
  const [tempSelectedProducts, setTempSelectedProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearchTimeout, setProductSearchTimeout] = useState(null);

  const fetchAllBanners = async () => {
    try {
      setLoadingBanners(true);
      const response = await bannerAPI.getAllBanners({ region: storeRegion });
      if (response.success) {
        setBanners(response.data || []);
      } else {
        toast.error(response.msg || 'Failed to load banners');
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
      toast.error('Failed to load banners');
    } finally {
      setLoadingBanners(false);
    }
  };

  useEffect(() => {
    if (!canView) return;
    fetchAllBanners();
  }, [canView, storeRegion]);

  useEffect(() => {
    return () => {
      if (productSearchTimeout) clearTimeout(productSearchTimeout);
    };
  }, [productSearchTimeout]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Source image must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (readerEvent) => {
        setCropperSrc(readerEvent.target.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleCropComplete = (croppedFile, previewUrl) => {
    if (croppedFile.size > 600 * 1024) {
      toast.error('Cropped image size exceeds 600KB limit. Please try again.');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      image: croppedFile,
      imagePreview: previewUrl,
    }));
    setShowCropper(false);
    setCropperSrc(null);
  };

  const handleCropperClose = () => {
    setShowCropper(false);
    setCropperSrc(null);
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'image/png') {
      toast.error('Icon must be a PNG file');
      return;
    }
    if (file.size > 600 * 1024) {
      toast.error('Icon size must be less than 600KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setFormData((prev) => ({
        ...prev,
        icon: file,
        iconPreview: ev.target.result,
        clearIcon: false,
      }));
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const resetForm = () => {
    if (formData.imagePreview && formData.imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(formData.imagePreview);
    }
    if (formData.iconPreview && formData.iconPreview.startsWith('blob:')) {
      URL.revokeObjectURL(formData.iconPreview);
    }
    setFormData({ ...emptyForm, region: storeRegion });
    setEditingBanner(null);
    setAllSelectedProducts([]);
    setTempSelectedProducts([]);
    setProductSearch('');
    setProducts([]);
  };

  const handleCreate = () => {
    resetForm();
    setFormData((prev) => ({ ...prev, region: storeRegion }));
    setShowDialog(true);
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    const productIds = (banner.products || []).map((p) => String(p.id || p.productId || p._id || p)).filter(Boolean);
    setFormData({
      ...emptyForm,
      title: banner.title || '',
      arabicTitle: banner.arTitle || banner.arabicTitle || '',
      description: banner.description || '',
      color: banner.color || '',
      link: banner.link || '',
      isActive: banner.isActive !== false,
      image: null,
      imagePreview: banner.image || null,
      icon: null,
      iconPreview: banner.icon || null,
      clearIcon: false,
      applicationType: productIds.length > 0 ? 'selected_products' : 'none',
      products: productIds,
      region: banner.region === 'india' ? 'india' : 'qatar',
    });
    setAllSelectedProducts([]);
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    resetForm();
  };

  const fetchProducts = async (searchQuery = '') => {
    try {
      setLoadingProducts(true);
      if (!searchQuery.trim() || searchQuery.trim().length < 2) {
        setProducts([]);
        setLoadingProducts(false);
        return;
      }
      const response = await productsAPI.getProductsList({
        page: 1,
        limit: 20,
        search: searchQuery.trim(),
        region: storeRegion,
      });
      if (response.success) {
        setProducts(response.data || []);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleProductSearchChange = (value) => {
    setProductSearch(value);
    if (productSearchTimeout) clearTimeout(productSearchTimeout);
    const timeout = setTimeout(() => fetchProducts(value), 350);
    setProductSearchTimeout(timeout);
  };

  const openProductDialog = () => {
    setTempSelectedProducts([...formData.products]);
    setProductSearch('');
    setProducts([]);
    setIsProductDialogOpen(true);
  };

  const handleProductToggle = (productId) => {
    const id = String(productId);
    setTempSelectedProducts((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
  };

  const saveProductSelection = () => {
    setFormData((prev) => ({
      ...prev,
      products: tempSelectedProducts,
    }));

    setAllSelectedProducts((prev) => {
      const map = new Map();
      prev.forEach((p) => map.set(String(p.productId || p._id), p));
      products.forEach((p) => {
        const id = String(p.productId || p._id);
        if (tempSelectedProducts.includes(id)) {
          map.set(id, p);
        }
      });
      return Array.from(map.values());
    });

    setIsProductDialogOpen(false);
  };

  const removeSelectedProduct = (productId) => {
    const id = String(productId);
    setFormData((prev) => ({
      ...prev,
      products: prev.products.filter((item) => String(item) !== id),
    }));
    setAllSelectedProducts((prev) =>
      prev.filter((p) => String(p.productId || p._id) !== id)
    );
  };

  const getSelectedProductInfo = () => {
    const selectedProducts = formData.products.map((id) =>
      allSelectedProducts.find((p) => String(p.productId || p._id) === String(id))
    ).filter(Boolean);

    return {
      products: selectedProducts,
      count: formData.products.length,
      displayText: formData.products.length > 0
        ? `${formData.products.length} product${formData.products.length !== 1 ? 's' : ''} selected`
        : 'No products selected',
    };
  };

  const handleSave = async () => {
    try {
      if (!formData.image && !formData.imagePreview) {
        toast.error('Please select a banner image');
        return;
      }

      if (formData.applicationType === 'selected_products' && formData.products.length < 1) {
        toast.error('Please select at least 1 product');
        return;
      }

      setSavingBanner(true);

      const payload = {
        ...formData,
        products: formData.applicationType === 'selected_products' ? formData.products : [],
        region: storeRegion,
      };

      let response;
      if (editingBanner) {
        response = await bannerAPI.updateBanner(editingBanner._id, payload);
      } else {
        response = await bannerAPI.createBanner(payload);
      }

      if (response.success) {
        toast.success(editingBanner ? 'Banner updated successfully!' : 'Banner created successfully!');
        handleCloseDialog();
        fetchAllBanners();
      } else {
        toast.error(response.msg || 'Failed to save banner');
      }
    } catch (error) {
      console.error('Error saving banner:', error);
      toast.error('Failed to save banner');
    } finally {
      setSavingBanner(false);
    }
  };

  const confirmDelete = async () => {
    if (!bannerToDelete) return;
    try {
      setDeleteLoading(true);
      const response = await bannerAPI.deleteBanner(bannerToDelete);
      if (response.success) {
        toast.success('Banner deleted successfully!');
        setShowDeleteDialog(false);
        setBannerToDelete(null);
        fetchAllBanners();
      } else {
        toast.error(response.msg || 'Failed to delete banner');
      }
    } catch (error) {
      toast.error('Failed to delete banner');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDragStart = (e, banner) => {
    setDraggedBanner(banner);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, banner) => {
    e.preventDefault();
    if (draggedBanner && draggedBanner._id !== banner._id) {
      setDragOverBanner(banner);
    }
  };

  const handleDrop = (e, targetBanner) => {
    e.preventDefault();
    if (!draggedBanner || draggedBanner._id === targetBanner._id) return;

    const newBanners = [...banners];
    const fromIndex = newBanners.findIndex((b) => b._id === draggedBanner._id);
    const toIndex = newBanners.findIndex((b) => b._id === targetBanner._id);
    if (fromIndex < 0 || toIndex < 0) return;

    const [removed] = newBanners.splice(fromIndex, 1);
    newBanners.splice(toIndex, 0, removed);
    setBanners(newBanners);
    setDraggedBanner(null);
    setDragOverBanner(null);
  };

  const saveReorder = async () => {
    try {
      const response = await bannerAPI.reorderBanners({
        banners: banners.map((b, index) => ({
          _id: b._id,
          order: index + 1,
        })),
      });
      if (response.success) {
        toast.success('Order saved');
        setIsReorderMode(false);
        fetchAllBanners();
      } else {
        toast.error(response.msg || 'Failed to save order');
      }
    } catch (error) {
      toast.error('Failed to save order');
    }
  };

  if (!canView) {
    return (
      <div className="banner-products-container">
        <div className="banner-empty-state">
          <p>You do not have permission to view banners.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="banner-products-container">
      <div className="banner-page-header">
        <div className="banner-page-title">
          <h1>Banners & Sections</h1>
          <p>Create and manage banners & sections</p>
        </div>
        <div className="banner-header-actions">
          {isReorderMode ? (
            <div className="banner-reorder-actions">
              <button className="banner-save-changes-btn" onClick={saveReorder}>
                <Save size={20} />
                Save Changes
              </button>
              <button className="banner-exit-reorder-btn" onClick={() => { setIsReorderMode(false); fetchAllBanners(); }}>
                <X size={20} />
                Exit Reorder
              </button>
            </div>
          ) : (
            <div className="banner-normal-actions">
              {canEdit && banners.length > 1 && (
                <button className="banner-reorder-mode-btn" onClick={() => setIsReorderMode(true)}>
                  <GripVertical size={20} />
                  Reorder
                </button>
              )}
              {canCreate && (
                <button className="banner-create-btn" onClick={handleCreate}>
                  <Plus size={20} />
                  Create New Banner
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {!isReorderMode && (
        <div className="banner-filters-row">
          <button className="banner-filter-pill active">
            Top Banner Slider ({banners.length})
          </button>
        </div>
      )}

      {isReorderMode && (
        <div className="banner-reorder-instructions">
          <p>💡 Drag and drop banners to reorder them. Click &quot;Save Changes&quot; when you&apos;re done.</p>
        </div>
      )}

      <div className="banner-banners-list-section">
        {loadingBanners ? (
          <div className="banner-loading-state">
            <div className="banner-loading-spinner"></div>
            <p>Loading banners...</p>
          </div>
        ) : banners.length === 0 ? (
          <div className="banner-empty-state">
            <p>No banners created yet</p>
          </div>
        ) : (
          <div className="banner-banners-grid">
            {banners.map((banner) => (
              <div
                key={banner._id}
                className={`banner-card ${isReorderMode ? 'reorder-mode' : ''} ${dragOverBanner?._id === banner._id ? 'drag-over' : ''}`}
                draggable={isReorderMode}
                onDragStart={(e) => handleDragStart(e, banner)}
                onDragOver={(e) => handleDragOver(e, banner)}
                onDrop={(e) => handleDrop(e, banner)}
                onDragEnd={() => { setDraggedBanner(null); setDragOverBanner(null); }}
              >
                {isReorderMode && (
                  <div className="banner-drag-handle">
                    <GripVertical size={20} />
                  </div>
                )}
                <div className="banner-image">
                  {banner.image ? (
                    <img src={banner.image} alt={banner.title || 'Banner'} />
                  ) : (
                    <div className="banner-no-image-placeholder">
                      <ImageIcon size={32} />
                      <span>No Image</span>
                    </div>
                  )}
                </div>
                <div className="banner-info">
                  <div className="banner-header">
                    <div className="banner-title-wrapper">
                      <h3 className="banner-title">{banner.title || '-'}</h3>
                    </div>
                    {!isReorderMode && (
                      <div className="banner-actions">
                        {canEdit && (
                          <button className="banner-edit-btn" onClick={() => handleEdit(banner)}>
                            <Edit size={14} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="banner-delete-btn"
                            onClick={() => { setBannerToDelete(banner._id); setShowDeleteDialog(true); }}
                          >
                            <Trash size={14} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="banner-subheader">
                    <span className="banner-type-badge slider">Top Banner Slider</span>
                  </div>
                  {(banner.products?.length > 0) && (
                    <div className="banner-meta">
                      <div className="banner-meta-item">
                        <strong>Products:</strong> {banner.products.length}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showDialog && (
        <div className="banner-dialog-overlay">
          <div className="banner-dialog">
            <div className="banner-dialog-header">
              <h2>{editingBanner ? 'Edit Banner' : 'Create Banner'}</h2>
              <button className="banner-dialog-close" onClick={handleCloseDialog}>
                <X size={20} />
              </button>
            </div>

            <div className="banner-dialog-content">
              <div className="banner-form-group">
                <label htmlFor="bannerType">Banner Type</label>
                <select
                  id="bannerType"
                  name="type"
                  value="banner_slider"
                  className="banner-form-select"
                  disabled
                >
                  <option value="banner_slider">Top Banner Slider</option>
                </select>
              </div>

              <div className="banner-form-group">
                <label htmlFor="bannerTitle">Title (Optional)</label>
                <input
                  type="text"
                  id="bannerTitle"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter banner title"
                  className="banner-form-input"
                />
              </div>

              <div className="banner-form-group">
                <label htmlFor="bannerArabic">Arabic Name (Optional)</label>
                <input
                  type="text"
                  id="bannerArabic"
                  value={formData.arabicTitle}
                  onChange={(e) => setFormData({ ...formData, arabicTitle: e.target.value })}
                  placeholder="أدخل اسم الفئة"
                  className="banner-form-input"
                  dir="rtl"
                />
              </div>

              <div className="banner-form-group">
                <label htmlFor="bannerImage">Banner Image (3:1 ratio)</label>
                <div className="banner-image-upload-section">
                  <div className="banner-image-preview-container">
                    {formData.imagePreview ? (
                      <div className="banner-image-preview">
                        <img src={formData.imagePreview} alt="Banner preview" />
                      </div>
                    ) : (
                      <div className="banner-image-placeholder">
                        <ImageIcon size={32} />
                        <span>No image selected</span>
                      </div>
                    )}
                  </div>

                  <div className="banner-image-upload-controls">
                    <input
                      type="file"
                      id="bannerImage"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="banner-file-input"
                    />
                    <label htmlFor="bannerImage" className="banner-upload-btn">
                      <Upload size={16} />
                      {formData.imagePreview ? 'Change Image' : 'Choose Image'}
                    </label>
                  </div>
                  <div className="banner-file-size-limit">
                    Maximum size: 600KB (3:1 ratio recommended)
                  </div>
                </div>
              </div>

              <div className="banner-form-group">
                <label htmlFor="bannerColor">Color</label>
                <div className="banner-color-input-wrapper">
                  <input
                    type="color"
                    id="bannerColorPicker"
                    value={formData.color || '#000000'}
                    onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
                    className="banner-color-picker"
                  />
                  <input
                    type="text"
                    id="bannerColor"
                    name="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    placeholder="#000000"
                    className="banner-form-input banner-color-text"
                  />
                  {formData.color && (
                    <button
                      type="button"
                      className="banner-clear-color-btn"
                      onClick={() => setFormData((prev) => ({ ...prev, color: '' }))}
                      title="Clear color"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>

              <div className="banner-form-group">
                <label htmlFor="bannerIcon">Icon (PNG only)</label>
                <div className="banner-image-upload-section">
                  <div className="banner-icon-preview-container">
                    {formData.iconPreview ? (
                      <div className="banner-icon-preview">
                        <img src={formData.iconPreview} alt="Icon preview" />
                        <button
                          type="button"
                          className="banner-clear-icon-btn"
                          onClick={() => setFormData((prev) => ({
                            ...prev,
                            icon: null,
                            iconPreview: null,
                            clearIcon: true,
                          }))}
                          title="Remove icon"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="banner-icon-placeholder">
                        <ImageIcon size={24} />
                        <span>No icon</span>
                      </div>
                    )}
                  </div>

                  <div className="banner-image-upload-controls">
                    <input
                      type="file"
                      id="bannerIcon"
                      accept="image/png"
                      onChange={handleIconChange}
                      className="banner-file-input"
                    />
                    <label htmlFor="bannerIcon" className="banner-upload-btn">
                      <Upload size={16} />
                      {formData.iconPreview ? 'Change Icon' : 'Choose Icon'}
                    </label>
                  </div>
                  <div className="banner-file-size-limit">
                    PNG only, max 600KB
                  </div>
                </div>
              </div>

              <div className="banner-form-group">
                <label htmlFor="bannerDescription">Description</label>
                <textarea
                  id="bannerDescription"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter banner description"
                  className="banner-form-textarea"
                  rows={3}
                />
              </div>

              <div className="banner-form-group">
                <label htmlFor="applicationType">Application Type</label>
                <div className="banner-radio-group">
                  <label className="banner-radio-label">
                    <input
                      type="radio"
                      name="applicationType"
                      value="none"
                      checked={formData.applicationType === 'none'}
                      onChange={handleInputChange}
                    />
                    None
                  </label>
                  <label className="banner-radio-label">
                    <input
                      type="radio"
                      name="applicationType"
                      value="selected_products"
                      checked={formData.applicationType === 'selected_products'}
                      onChange={handleInputChange}
                    />
                    Select Products
                  </label>
                </div>
              </div>

              {formData.applicationType === 'selected_products' && (
                <div className="banner-form-group">
                  <div className="banner-choose-btn-container">
                    <button
                      type="button"
                      className="banner-choose-btn"
                      onClick={openProductDialog}
                    >
                      Choose Products
                    </button>
                  </div>
                  <div className="banner-selected-products-section">
                    <div className="banner-selected-products-header">
                      <span className="banner-products-count">
                        {getSelectedProductInfo().displayText}
                      </span>
                      {getSelectedProductInfo().count > 0 && (
                        <button
                          type="button"
                          className="banner-view-products-btn"
                          onClick={() => setIsProductViewDialogOpen(true)}
                        >
                          View Products
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="banner-dialog-footer">
              <button className="banner-cancel-btn" onClick={handleCloseDialog}>
                Cancel
              </button>
              <button
                className="banner-save-btn"
                onClick={handleSave}
                disabled={savingBanner}
              >
                {savingBanner ? (editingBanner ? 'Updating...' : 'Creating...') : (editingBanner ? 'Update Banner' : 'Create Banner')}
              </button>
            </div>
          </div>
        </div>
      )}

      {isProductDialogOpen && (
        <div className="banner-dialog-overlay">
          <div className="banner-product-selection-dialog">
            <div className="banner-dialog-header">
              <h2>Select Products</h2>
              <button className="banner-close-btn" onClick={() => setIsProductDialogOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="banner-dialog-content">
              <div className="banner-product-search-section">
                <div className="banner-search-input-container">
                  <input
                    type="text"
                    className="banner-form-input"
                    placeholder="Search products (min 2 chars)..."
                    value={productSearch}
                    onChange={(e) => handleProductSearchChange(e.target.value)}
                  />
                </div>
                {loadingProducts ? (
                  <div className="banner-loading-state">
                    <div className="banner-loading-spinner" />
                    <p>Searching...</p>
                  </div>
                ) : (
                  <div className="banner-product-results">
                    {products.map((product) => {
                      const id = String(product.productId || product._id);
                      const checked = tempSelectedProducts.includes(id);
                      const thumb = product.images?.[0]?.thumbnail || product.images?.[0]?.image || '';
                      return (
                        <label key={product._id} className="banner-product-result-item">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleProductToggle(id)}
                          />
                          {thumb ? <img src={thumb} alt={product.name} /> : <div className="banner-product-no-img" />}
                          <div>
                            <div className="banner-product-name">{product.name}</div>
                            <div className="banner-product-meta">ID: {product.productId} · {formatPrice(product.rate)}</div>
                          </div>
                        </label>
                      );
                    })}
                    {!loadingProducts && productSearch.length >= 2 && products.length === 0 && (
                      <p className="banner-empty-state">No products found</p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="banner-dialog-footer">
              <button className="banner-cancel-btn" onClick={() => setIsProductDialogOpen(false)}>Cancel</button>
              <button className="banner-save-btn" onClick={saveProductSelection}>
                Save Selection ({tempSelectedProducts.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {isProductViewDialogOpen && (
        <div className="banner-dialog-overlay" onClick={() => setIsProductViewDialogOpen(false)}>
          <div className="banner-product-view-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="banner-dialog-header">
              <h2>Selected Products</h2>
              <button className="banner-dialog-close" onClick={() => setIsProductViewDialogOpen(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="banner-dialog-content">
              {getSelectedProductInfo().products.length === 0 ? (
                <p className="banner-empty-state">No product details loaded. Re-select products to view details.</p>
              ) : (
                getSelectedProductInfo().products.map((product) => {
                  const id = String(product.productId || product._id);
                  const thumb = product.images?.[0]?.thumbnail || product.images?.[0]?.image || '';
                  return (
                    <div key={id} className="banner-selected-product-item">
                      {thumb ? <img src={thumb} alt={product.name} /> : <div className="banner-product-no-img" />}
                      <div className="banner-selected-product-details">
                        <span>{product.name}</span>
                        <span>ID: {product.productId}</span>
                      </div>
                      <button type="button" className="banner-delete-btn" onClick={() => removeSelectedProduct(id)}>
                        <Trash size={14} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
            <div className="banner-dialog-footer">
              <button className="banner-save-btn" onClick={() => setIsProductViewDialogOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteDialog && (
        <div className="banner-dialog-overlay">
          <div className="banner-dialog">
            <div className="banner-dialog-header">
              <h2>Confirm Delete</h2>
              <button className="banner-dialog-close" onClick={() => setShowDeleteDialog(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="banner-dialog-content">
              <p>Are you sure you want to delete this banner? This action cannot be undone.</p>
            </div>
            <div className="banner-dialog-footer">
              <button className="banner-cancel-btn" onClick={() => setShowDeleteDialog(false)} disabled={deleteLoading}>
                Cancel
              </button>
              <button className="banner-delete-confirm-btn" onClick={confirmDelete} disabled={deleteLoading}>
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCropper && cropperSrc && (
        <ImageCropperModal
          imageSrc={cropperSrc}
          aspectRatio={3}
          onCropComplete={handleCropComplete}
          onClose={handleCropperClose}
        />
      )}
    </div>
  );
};

export default Banners;
